import type { WineRecommendation } from '@/common/generated/wine-dish-recommendation.typebox';
import { RequireAuth } from '@/components/PermissionLink';
import { EditOutlined, GlobalOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Space } from 'antd';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { usePairingStore } from '../store/PairingContext';
import { DishAnalysisCard } from './DishAnalysisCard';
import { ProductSearchResults } from './ProductSearchResults';
import { WineDetailDrawer } from './WineDetailDrawer';
import { WineRecommendationCard } from './WineRecommendationCard';

export const PairingView = observer(() => {
  const [selectedWine, setSelectedWine] = useState<WineRecommendation>();
  const [isTogglingPublic, setIsTogglingPublic] = useState(false);
  const pairingStore = usePairingStore();

  if (!pairingStore.currentSelection) return null;

  const pairing =
    pairingStore.currentSelection.type === 'menu'
      ? pairingStore.currentSelection.menu.pairing
      : pairingStore.currentSelection.pairing;

  if (!pairing) return null;

  const analysis = pairing.analysis_data?.dish_analysis;
  const wineRecommendation = pairing.analysis_data?.wine_recommendation;
  const recommendations = wineRecommendation?.recommendations ?? [];
  const products = pairing.analysis_data?.products ?? [];

  if (!analysis) return null;

  const handleTogglePublic = async () => {
    setIsTogglingPublic(true);
    try {
      await pairingStore.togglePublic([pairing.id]);
      await pairingStore.refreshCurrentPairing();
    } finally {
      setIsTogglingPublic(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{analysis.name}</h1>
          {analysis.cuisine && (
            <p className="text-sm text-muted-foreground">
              Cuisine: {analysis.cuisine}
            </p>
          )}
        </div>
        <Space>
          <Button
            icon={<GlobalOutlined />}
            onClick={handleTogglePublic}
            type={pairing.public ? 'primary' : 'default'}
            loading={isTogglingPublic}
          >
            {pairing.public ? 'Public' : 'Private'}
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => pairingStore.setPairingFormVisible(true)}
          >
            Edit
          </Button>
        </Space>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Dish Analysis</h3>
        <DishAnalysisCard analysis={analysis} />
      </div>

      <Divider />

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Summary</h3>
        {wineRecommendation?.summary.highlights.length &&
        wineRecommendation?.summary.highlights.length > 0 ? (
          <Card
            classNames={{
              body: 'space-y-4',
            }}
          >
            {wineRecommendation?.summary.highlights.map((highlight) => (
              <p key={highlight} className="text-sm text-muted-foreground">
                {highlight}
              </p>
            ))}
          </Card>
        ) : (
          <div className="text-center text-gray-500">
            No summary found, please edit the pairing to generate a summary
          </div>
        )}
      </div>

      <Divider />

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Recommended Wines</h3>
        {recommendations.length && recommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendations.map((wine, index) => (
              <WineRecommendationCard
                key={wine.productId}
                wine={wine}
                rank={index + 1}
                onClick={() => setSelectedWine(wine)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500">
            No recommendations found, please edit the pairing to generate
            recommendations
          </div>
        )}
      </div>

      <Divider />

      <RequireAuth permissions={['admin']} fallback={null}>
        <ProductSearchResults products={products} />
      </RequireAuth>

      <WineDetailDrawer
        open={!!selectedWine}
        onClose={() => setSelectedWine(undefined)}
        wine={selectedWine}
        dishAnalysis={analysis}
        alternativeWines={
          selectedWine
            ? recommendations.filter(
                (w) => w.productId !== selectedWine.productId,
              )
            : []
        }
        onWineSelect={setSelectedWine}
      />
    </div>
  );
});
